import * as Popover from '@radix-ui/react-popover'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { MouseEvent, ReactNode, useEffect, useRef, useState } from 'react'
import { ContainerElement } from 'types'

interface Props extends ContainerElement {
  trigger?: ReactNode
}

export function ExtendedNavContainer({ children, className, trigger }: Props) {
  const [open, setOpen] = useState(false)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const router = useRouter()

  useEffect(() => {
    const close = () => {
      setOpen(false)
    }

    router.events.on('routeChangeStart', close)
    return () => {
      router.events.off('routeChangeStart', close)
    }
  }, [router.events])

  function show() {
    setOpen(true)
  }

  function hide() {
    setTimeout(() => {
      setOpen(false)
    }, 5)
  }

  function onTriggerMouseLeave(event: MouseEvent<HTMLButtonElement>) {
    if (
      contentRef.current === event.relatedTarget ||
      triggerRef.current === event.relatedTarget ||
      (event.relatedTarget instanceof Node &&
        triggerRef.current?.contains(event.relatedTarget))
    ) {
      show()
      return
    }

    hide()
  }

  function onContentMouseLeave(event: MouseEvent<HTMLDivElement>) {
    if (
      triggerRef.current === event.relatedTarget ||
      (event.relatedTarget instanceof Node &&
        triggerRef.current?.contains(event.relatedTarget))
    ) {
      show()
      return
    }

    hide()
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Portal>
        <Popover.Content
          ref={contentRef}
          className='z-[51] w-screen bg-transparent pt-5 outline-none'
          onMouseLeave={onContentMouseLeave}
        >
          <div
            className={classNames(
              className,
              'border-t border-cool-300 bg-cool-50 py-6 shadow-lg'
            )}
          >
            <div className='container'>{children}</div>
          </div>
        </Popover.Content>
      </Popover.Portal>

      <Popover.Trigger
        ref={triggerRef}
        onMouseEnter={show}
        onMouseMove={show}
        onMouseOut={onTriggerMouseLeave}
        className='outline-none'
        data-extended-nav-trigger
      >
        {trigger}
      </Popover.Trigger>
    </Popover.Root>
  )
}
